import {
  IpmErrorBoundary,
  reportIpmAction,
  reportIpmDismiss,
  reportIpmVisible,
  type IpmMetricTags,
  useIpmRenderTiming,
} from '@github-ui/ipm-telemetry'
import {verifiedFetch} from '@github-ui/verified-fetch'
import {XIcon} from '@primer/octicons-react'
import {IconButton, Link} from '@primer/react'
import {type ReactNode, Profiler, useEffect, useMemo, useState} from 'react'
import type {BannerSegment, BannerAnalytics} from '@github-ui/code-view-types'
import styles from './BannerNudgeCard.module.css'
import {CommandPicker, type InstallationMethod, type CommandPickerFooter} from './CommandPicker'

export interface BannerNudgeAnalytics extends BannerAnalytics {
  ctaAction: string
  entityId?: number
}

export interface BannerNudgeCardProps {
  title: string
  segments: BannerSegment[]
  icon: ReactNode
  ctaTitle?: string
  ctaHref?: string
  ctaPrimary?: boolean
  stacked?: boolean
  /**
   * Visual layout style.
   * - `settings` (default): renders as a `Box-header`-style row designed to live inside
   *   a parent `.Box` wrapper (its left/right borders rely on the parent's border).
   *   Used for settings/policy pages where multiple cards stack together.
   *   Renders the title as `<h2>` to fit the typical settings-page heading hierarchy
   *   (page-level `<h1>` followed by `<h2>` sections).
   * - `dashboard`: renders as a standalone bordered card with its own 4-sided border,
   *   padding, and background. Use when the nudge is rendered on its own (e.g. the
   *   dashboard) without a parent `.Box` wrapper. Renders the title as `<h3>` to match
   *   the original ERB markup, the existing `.ipm-nudge-content h3` styles in
   *   `nudges.scss`, and a dashboard hierarchy of `<h1>` → `<h2>` section → `<h3>` nudge.
   */
  displayStyle?: 'settings' | 'dashboard'
  /**
   * POST endpoint for the dismiss action. Required to render the dismiss X.
   * Omit (or pass `null`/`undefined`) to suppress the dismiss control entirely
   * — used by nudges that must not offer a global dismiss (e.g. the
   * `raf_cb` org-settings nudge once Copilot Business is enabled; the admin
   * must resolve each pending member request individually rather than hiding
   * the whole card).
   */
  dismissPath?: string | null
  analytics: BannerNudgeAnalytics
  testSelector: string
  installationMethods?: InstallationMethod[]
  defaultInstallationMethod?: string
  footer?: CommandPickerFooter
}

function getSegmentKey(segment: BannerSegment, index: number): string {
  return `${segment.type}-${index}-${segment.content.slice(0, 20)}`
}

export function BannerNudgeCard({
  title,
  segments,
  icon,
  ctaTitle,
  ctaHref,
  ctaPrimary = true,
  stacked = false,
  displayStyle = 'settings',
  dismissPath,
  analytics,
  testSelector,
  installationMethods,
  defaultInstallationMethod,
  footer,
}: BannerNudgeCardProps) {
  const [hidden, setHidden] = useState(false)
  const telemetryTags = useMemo<IpmMetricTags>(
    () => ({
      package: 'in-product-targeting',
      component: 'BannerNudgeCard',
      surface: 'banner',
      display_location: analytics.location,
      category: analytics.category,
      nudge_id: testSelector,
      variant: displayStyle,
    }),
    [analytics.category, analytics.location, displayStyle, testSelector],
  )
  const renderTiming = useIpmRenderTiming(telemetryTags)

  useEffect(() => {
    reportIpmVisible(telemetryTags)
  }, [telemetryTags])

  const handleDismiss = async () => {
    reportIpmDismiss(telemetryTags)
    if (!dismissPath) {
      setHidden(true)
      return
    }
    try {
      await verifiedFetch(dismissPath, {method: 'POST'})
    } catch {
      reportIpmAction('error', telemetryTags, {samplingProbability: 1})
      // Silently handle network errors to maintain good UX
    } finally {
      setHidden(true)
    }
  }

  if (hidden) {
    return null
  }

  const isDashboard = displayStyle === 'dashboard'
  // The `settings` display style uses `Box-header`, which has negative left/right/top
  // margins designed to overlap a parent `.Box` border, so we inject the bottom border
  // + top corner radii inline when the card isn't stacked. The `dashboard` display
  // style's CSS (`.ipm-nudge-box.dashboard` in nudges.scss) already supplies a full
  // standalone border, padding, and background, so no inline styles or `Box-header`
  // class are needed.
  const containerClassName = isDashboard ? 'ipm-nudge-box dashboard' : 'ipm-nudge-box settings Box-header'
  const containerStyle =
    !isDashboard && !stacked
      ? {
          borderBottom: 'var(--borderWidth-thin) solid var(--borderColor-default)',
          borderTopLeftRadius: 'var(--borderRadius-medium)',
          borderTopRightRadius: 'var(--borderRadius-medium)',
        }
      : undefined

  const card = (
    <>
      <span data-test-selector={testSelector} />
      <div
        className={containerClassName}
        style={containerStyle}
        data-hpc
        data-testid="task-nudge-card"
        data-analytics-visible={JSON.stringify({
          category: analytics.category,
          action: 'visible',
          label: `group:${analytics.group};location:${analytics.location}`,
        })}
      >
        <div className={styles.IconWrapper} aria-hidden="true">
          {icon}
        </div>

        {/* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */}
        <div className="ipm-nudge-content">
          {isDashboard ? <h3>{title}</h3> : <h2>{title}</h2>}
          <p>
            {segments.map((segment, index) =>
              segment.type === 'link' && 'href' in segment ? (
                <Link key={getSegmentKey(segment, index)} href={segment.href} className="Link--inTextBlock">
                  {segment.content}
                </Link>
              ) : (
                <span key={getSegmentKey(segment, index)}>{segment.content}</span>
              ),
            )}
          </p>
          {installationMethods && installationMethods.length > 0 && (
            <CommandPicker
              installationMethods={installationMethods}
              defaultInstallationMethod={defaultInstallationMethod}
              footer={footer}
              telemetryTags={telemetryTags}
              analytics={{
                category: analytics.category,
                label: `group:${analytics.group};location:${analytics.location}`,
              }}
            />
          )}
        </div>

        {ctaTitle && ctaHref && !installationMethods?.length && (
          <a
            href={ctaHref}
            onClick={() => reportIpmAction('cta_click', telemetryTags)}
            /* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */
            className={`ipm-nudge-cta btn btn-sm${ctaPrimary ? ' btn-primary' : ''}`}
            data-test-selector={testSelector}
            data-analytics-event={JSON.stringify({
              category: analytics.category,
              action: analytics.ctaAction,
              label: `group:${analytics.group};location:${analytics.location}`,
            })}
          >
            {ctaTitle}
          </a>
        )}

        {dismissPath && (
          /* eslint-disable-next-line @github-ui/github-monorepo/no-global-classname-literals */
          <div className="ipm-nudge-dismiss">
            <IconButton
              icon={XIcon}
              variant="invisible"
              aria-label="Dismiss"
              onClick={handleDismiss}
              data-analytics-event={JSON.stringify({
                category: analytics.category,
                action: 'dismiss',
                label: `group:${analytics.group};location:${analytics.location}`,
              })}
            />
          </div>
        )}
      </div>
    </>
  )

  const content = !renderTiming.isEnabled ? (
    card
  ) : (
    <Profiler id="BannerNudgeCard" onRender={renderTiming.onRender}>
      {card}
    </Profiler>
  )

  return (
    <IpmErrorBoundary tags={telemetryTags} boundaryName="BannerNudgeCard">
      {content}
    </IpmErrorBoundary>
  )
}
